import React from 'react';
import {
  Box,
  Typography,
  Grid,
  LinearProgress,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Cpu,
  MemoryStick,
  Thermometer,
  Clock,
  Download,
  Upload,
  HardDrive
} from 'lucide-react';
import { fadeInUp } from '../styles/sharedStyles';

// Helper functions
const formatBytesPerSecond = (bytesPerSecond) => {
  if (!bytesPerSecond || bytesPerSecond === 0) return '0 B/s';
  const k = 1024;
  const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s'];
  const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k));
  return `${(bytesPerSecond / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatBytes = (bytes) => {
  if (bytes == null || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

const formatUptime = (seconds) => {
  if (!seconds) return '—';
  const d = Math.floor(seconds / (3600 * 24));
  const h = Math.floor((seconds % (3600 * 24)) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const getMetricColor = (value, metricType) => {
  if (metricType === 'cpu' || metricType === 'ram') {
    if (value > 80) return '#ff4444';
    if (value > 60) return '#ffae00';
    if (value > 30) return '#ffeb3b';
    return '#00d4ff';
  }
  if (metricType === 'temp') {
    if (value > 55) return '#ff4444';
    if (value > 40) return '#ffae00';
    return '#00d4ff';
  }
  if (metricType === 'disk') {
    if (value > 90) return '#ff4444';
    if (value > 75) return '#ffae00';
    if (value > 50) return '#ffeb3b';
    return '#00ff88';
  }
  if (metricType === 'network' || metricType === 'disk_io') {
    const threshold = 1024 * 1024;
    if (value > 10 * threshold) return '#ff4444';
    if (value > 5 * threshold) return '#ffae00';
    if (value > threshold) return '#ffeb3b';
    return '#00d4ff';
  }
  return '#00d4ff';
};

// Styled Components
const UtilizationCard = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  borderRadius: `${(theme.custom?.cardRounding || 28) * 0.7}px`,
  padding: '20px',
  border: '1px solid rgba(255, 255, 255, 0.06)',
  backdropFilter: `blur(${(theme.custom?.glassBlur || 20) * 0.5}px)`,
  transition: 'all 0.3s ease',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: 160,
  height: '100%',
  '&:hover': {
    backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05))',
    borderColor: 'rgba(255, 255, 255, 0.15)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: 14,
    borderRadius: `${(theme.custom?.cardRounding || 28) * 0.5}px`,
    minHeight: 140,
  },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
}));

const TitleSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const StatusIndicator = styled(Box)(({ isOnline }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  background: isOnline
    ? 'linear-gradient(135deg, #00ff88, #00d4aa)'
    : 'linear-gradient(135deg, #ff4444, #ff6b6b)',
  boxShadow: isOnline
    ? '0 0 15px rgba(0, 255, 136, 0.5)'
    : '0 0 15px rgba(255, 68, 68, 0.5)',
}));

const StatusBadge = styled(Box)(({ badgeColor }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  background: `${badgeColor}15`,
  border: `1px solid ${badgeColor}30`,
  borderRadius: '14px',
  padding: '6px 12px',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: `${badgeColor}25`,
    borderColor: `${badgeColor}50`,
  },
}));

const MetricGlow = styled(Box)(({ color }) => ({
  height: '3px',
  width: '100%',
  borderRadius: '2px',
  background: color,
  boxShadow: `0 0 12px ${color}`,
  marginTop: '8px',
  opacity: 0.6,
}));

const StatRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '10px',
  paddingTop: '10px',
  borderTop: '1px solid rgba(255, 255, 255, 0.06)',
});

const DebianSysmon = ({ rpiStatus, loading }) => {
  const theme = useTheme();

  if (loading && !rpiStatus) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320, color: 'rgba(255,255,255,0.5)' }}>
        <Typography>Loading system stats…</Typography>
      </Box>
    );
  }

  if (!rpiStatus) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, color: 'rgba(255,255,255,0.6)' }}>
        <Typography variant="body1">System stats unavailable. Ensure the app runs on the Debian host with /proc and /sys access.</Typography>
      </Box>
    );
  }

  const {
    cpu_usage = 0,
    memory_usage = 0,
    temperature = 0,
    uptime = 0,
    loadavg = [],
    mem_total_kb = 0,
    mem_available_kb = 0,
    cpu_count = 0,
    cpu_mhz = 0,
    network_rx = 0,
    network_tx = 0,
    disk_read = 0,
    disk_write = 0,
    disk_total = 0,
    disk_used = 0,
    disk_free = 0,
    disk_percent = 0,
    last_checked
  } = rpiStatus;

  const memUsedKb = mem_total_kb ? mem_total_kb - mem_available_kb : 0;
  const load1 = loadavg[0] ?? 0;
  const load5 = loadavg[1] ?? 0;
  const load15 = loadavg[2] ?? 0;

  const cpuColor = getMetricColor(cpu_usage, 'cpu');
  const ramColor = getMetricColor(memory_usage, 'ram');
  const tempColor = getMetricColor(temperature, 'temp');
  const diskSpaceColor = getMetricColor(disk_percent, 'disk');
  const netColor = getMetricColor(network_rx, 'network');
  const diskIoColor = getMetricColor(disk_read, 'disk_io');

  return (
    <Box sx={{ py: 1 }}>
      {/* Header */}
      <HeaderBox>
        <TitleSection>
          <Box
            component="img"
            src="/raspberry-pi.png"
            alt="Debian"
            sx={{ width: 36, height: 36, objectFit: 'contain' }}
          />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
              Debian 13 – System Monitor
            </Typography>
            {last_checked && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                Updated from host
              </Typography>
            )}
          </Box>
        </TitleSection>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Green Uptime Badge */}
          <StatusBadge badgeColor="#00ff88">
            <Clock size={12} style={{ color: '#00ff88' }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#00ff88' }}>
              {formatUptime(uptime)}
            </Typography>
          </StatusBadge>
          
          {/* Temperature Badge */}
          <StatusBadge badgeColor={tempColor}>
            <Thermometer size={12} style={{ color: tempColor }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: tempColor }}>
              {temperature}°C
            </Typography>
          </StatusBadge>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StatusIndicator isOnline={true} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Online
            </Typography>
          </Box>
        </Box>
      </HeaderBox>

      {/* All Cards in One Grid */}
      <Grid container spacing={2.5}>
        {/* CPU Card */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <UtilizationCard>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Cpu size={18} style={{ color: cpuColor }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                  CPU
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                {cpu_count} cores • {cpu_mhz} MHz
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.5, display: 'block' }}>
              Load: {load1.toFixed(2)} / {load5.toFixed(2)} / {load15.toFixed(2)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h3" sx={{ fontWeight: 800, color: cpuColor, textShadow: `0 0 20px ${cpuColor}44` }}>
                {cpu_usage}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, cpu_usage)}
              sx={{
                mt: 1.5,
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: cpuColor,
                  borderRadius: 3,
                  boxShadow: `0 0 10px ${cpuColor}66`,
                },
              }}
            />
          </UtilizationCard>
        </Grid>

        {/* RAM Card */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <UtilizationCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <MemoryStick size={18} style={{ color: ramColor }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                RAM
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h3" sx={{ fontWeight: 800, color: ramColor, textShadow: `0 0 20px ${ramColor}44` }}>
                {memory_usage}%
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5, display: 'block' }}>
              {formatBytes(memUsedKb * 1024)} / {formatBytes(mem_total_kb * 1024)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, memory_usage)}
              sx={{
                mt: 1.5,
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(255,255,255,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: ramColor,
                  borderRadius: 3,
                  boxShadow: `0 0 10px ${ramColor}66`,
                },
              }}
            />
          </UtilizationCard>
        </Grid>

        {/* Network Card - DL and UL together */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <UtilizationCard>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Download size={18} style={{ color: netColor }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                Network
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Download size={14} style={{ color: netColor }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 60 }}>Download</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: netColor }}>{formatBytesPerSecond(network_rx)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Upload size={14} style={{ color: netColor }} />
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 60 }}>Upload</Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: netColor }}>{formatBytesPerSecond(network_tx)}</Typography>
              </Box>
            </Box>
            <MetricGlow color={netColor} />
          </UtilizationCard>
        </Grid>

        {/* Disk Card - Space + IO together */}
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <UtilizationCard>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HardDrive size={18} style={{ color: diskSpaceColor }} />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 500 }}>
                  Disk
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 800, color: diskSpaceColor }}>{disk_percent}%</Typography>
            </Box>
            
            {/* Disk Space */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#fff' }}>
                {formatBytes(disk_used)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>/ {formatBytes(disk_total)}</Typography>
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
              Free: {formatBytes(disk_free)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, disk_percent)}
              sx={{
                mt: 1,
                height: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(255,255,255,0.1)',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: diskSpaceColor,
                  borderRadius: 2,
                  boxShadow: `0 0 8px ${diskSpaceColor}66`,
                },
              }}
            />
            
            {/* Disk I/O - Read/Write */}
            <StatRow>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>R:</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: diskIoColor }}>{formatBytesPerSecond(disk_read)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>W:</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: diskIoColor }}>{formatBytesPerSecond(disk_write)}</Typography>
              </Box>
            </StatRow>
          </UtilizationCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DebianSysmon;
